import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/** Relectures après un retour du portail : tout de suite, puis le temps qu'un webhook arrive. */
export const PORTAL_REFRESH_DELAYS_MS = [0, 3_000, 8_000]

/**
 * Le retour du portail client Stripe (`?portal=return`, #353). Ce qui y a changé
 * revient par webhook, parfois une fois la page déjà chargée : on relit l'écran
 * quelques fois, puis on retire le paramètre pour qu'un rechargement ne rejoue rien.
 */
export function usePortalReturn(refresh: () => Promise<unknown>): void {
  const [params, setParams] = useSearchParams()
  // Lu au montage : retirer le paramètre ne doit pas annuler les relectures en cours.
  const [returning] = useState(() => params.get('portal') === 'return')
  const latest = useRef(refresh)
  useEffect(() => {
    latest.current = refresh
  })

  useEffect(() => {
    if (!returning) return
    // Un échec réseau n'interrompt rien : la relecture suivante réessaie.
    const refresh = () => void latest.current().catch(() => undefined)
    let step = 0
    let timer = setTimeout(function next() {
      refresh()
      step += 1
      const delay = PORTAL_REFRESH_DELAYS_MS[step]
      if (delay !== undefined) timer = setTimeout(next, delay - PORTAL_REFRESH_DELAYS_MS[step - 1]!)
    }, PORTAL_REFRESH_DELAYS_MS[0])
    setParams((current) => {
      current.delete('portal')
      return current
    }, { replace: true })
    return () => clearTimeout(timer)
  }, [returning, setParams])
}
